import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import { ChatMessage, ChatState } from './model';
import { CHAT_LOADERS } from './utils';

export const selectChatState = (state: RootState) => state.chat;

export const selectChatFilter = createSelector(
  selectChatState,
  ({ chatFilter }: ChatState) => chatFilter,
);

export const selectFilteredChats = createSelector(
  selectChatState,
  ({ chats, chatFilter }: ChatState) => {
    if (chatFilter === 'all') return chats;
    return chats.filter(({ unReadCount }) =>
      chatFilter === 'new' ? unReadCount > 0 : !unReadCount,
    );
  },
);

export const makeSelectChatLoader = (loader: CHAT_LOADERS) =>
  createSelector(
    selectChatState,
    ({ loaders }: ChatState) => !!loaders?.[loader],
  );

export const selectCurrentChatMessagesByDate = createSelector(
  selectChatState,
  ({ currentChat }: ChatState) => {
    if (currentChat) {
      const messagesByDate = currentChat.messages.reduce(
        (groups, message) => {
          const date = new Date(message.createdAt).toLocaleDateString('it-IT', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          });

          return { ...groups, [date]: [...(groups[date] ?? []), message] };
        },
        {} as Record<string, ChatMessage[]>,
      );

      const sortedDates = Object.keys(messagesByDate).sort((a, b) => {
        return (
          new Date(messagesByDate[a][0].createdAt).getTime() -
          new Date(messagesByDate[b][0].createdAt).getTime()
        );
      });
      return { messagesByDate, sortedDates };
    }
    return { messagesByDate: {}, sortedDates: [] };
  },
);

export const selectOtherUser = createSelector(
  selectChatState,
  ({ currentChat }: ChatState) => currentChat?.otherUser,
);

export const selectCurrentChatId = createSelector(
  selectChatState,
  ({ currentChat }: ChatState) => currentChat?.chatId ?? '',
);
